import React, { useState, useEffect } from 'react';
import { X, Save, Percent, FileText, Plus, Trash2 } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const TaxCategoryModal = ({ isOpen, onClose, orgId }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';
  const [taxes, setTaxes] = useState([]);
  const [newTax, setNewTax] = useState({ taxid: '', description: '', taxrate: 0 });

  // جلب الـ Org ID: إما ييجي من الـ props أو من الـ session
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const activeOrgId = orgId || session.org_id;

  useEffect(() => {
    if (isOpen && activeOrgId) {
        fetchTaxes();
    }
  }, [isOpen, activeOrgId]);

  const fetchTaxes = async () => {
    try {
      const res = await axios.get(`/api/tax-categories/${activeOrgId}`);
      setTaxes(res.data);
    } catch (err) {
      console.error("Fetch Taxes Error:", err);
    }
  };

  const handleSave = async () => {
    if (!newTax.taxid) return toast.error(t('taxCategoryModal.taxIdRequired'));
    if (newTax.taxrate < 0) return toast.error(t('taxCategoryModal.invalidRate'));

    try {
      // ملحوظة مهمة: لازم نبعت org_id صريح مع البيانات مش نعتمد بس على الـ payload
      const payload = {
        taxid: newTax.taxid.toUpperCase(),
        description: newTax.description || newTax.taxid,
        taxrate: parseFloat(newTax.taxrate),
        org_id: activeOrgId // تأكيد إرسال org_id
      };

      const token = localStorage.getItem('token');
      await axios.post('/api/tax-categories/save', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('taxCategoryModal.saveSuccess'));
      setNewTax({ taxid: '', description: '', taxrate: 0 });
      fetchTaxes();
    } catch (err) {
      toast.error(t('taxCategoryModal.saveFailed', { detail: err.response?.data?.detail || t('taxCategoryModal.serverError') }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[8000] flex items-center justify-center bg-black/60 backdrop-blur-sm" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="bg-surface-card w-[500px] rounded-lg shadow-2xl flex flex-col overflow-hidden border-t-4 border-t-primary">
        {/* Header */}
        <div className="bg-surface-panel p-4 border-b border-line flex justify-between items-center">
          <div className="flex flex-col">
             <h2 className="font-black text-primary uppercase flex items-center gap-2">
                <Percent size={16}/> {t('taxCategoryModal.title')}
             </h2>
             <span className="text-[8px] text-ink-secondary font-bold uppercase">{t('taxCategoryModal.orgContext', { org: activeOrgId })}</span>
          </div>
          <button onClick={onClose} className="hover:text-status-danger transition-colors"><X size={18}/></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Input Section */}
          <div className="grid grid-cols-3 gap-2 bg-primary-soft p-3 rounded-md border border-primary-soft items-end">
            <div className="flex flex-col gap-1">
                <label className="text-[8px] font-black text-ink-secondary uppercase">{t('taxCategoryModal.taxId')}</label>
                <input placeholder="VAT15" className="p-2 border rounded font-black uppercase text-xs outline-none focus:border-primary"
                    value={newTax.taxid} onChange={e => setNewTax({...newTax, taxid: e.target.value})} />
            </div>
            <div className="flex flex-col gap-1">
                <label className="text-[8px] font-black text-ink-secondary uppercase">{t('taxCategoryModal.ratePercent')}</label>
                <input placeholder="15" type="number" className="p-2 border rounded font-black text-xs outline-none focus:border-primary"
                    value={newTax.taxrate} onChange={e => setNewTax({...newTax, taxrate: e.target.value})} />
            </div>
            <button onClick={handleSave} className="bg-primary-dark text-ink-inverse font-black rounded py-2.5 flex items-center gap-1 justify-center hover:bg-primary transition-all">
                <Plus size={14}/> {t('taxCategoryModal.add')}
            </button>
          </div>

          {/* Table Section */}
          <div className="max-h-60 overflow-y-auto border border-line rounded shadow-inner">
            <table className={`w-full text-[10px] ${isRTL ? 'text-right' : 'text-left'}`}>
              <thead className="bg-surface-panel font-black text-ink-secondary uppercase sticky top-0">
                <tr>
                    <th className="p-3 border-r border-line">{t('taxCategoryModal.taxId')}</th>
                    <th className="p-3 border-r border-line text-center">{t('taxCategoryModal.ratePercent')}</th>
                    <th className={`p-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('taxCategoryModal.action')}</th>
                </tr>
              </thead>
              <tbody>
                {taxes.map((tax, i) => (
                  <tr key={i} className="border-b border-line-subtle hover:bg-primary-soft font-bold transition-colors">
                    <td className="p-3 text-primary border-r border-line">{tax.taxid}</td>
                    <td className="p-3 text-status-danger text-center border-r border-line font-mono">{tax.taxrate}%</td>
                    <td className={`p-3 ${isRTL ? 'text-left' : 'text-right'}`}>
                        <button className="text-status-danger/40 hover:text-status-danger transition-colors"><Trash2 size={12}/></button>
                    </td>
                  </tr>
                ))}
                {taxes.length === 0 && (
                    <tr><td colSpan="3" className="p-10 text-center text-ink-secondary italic uppercase">{t('taxCategoryModal.noTaxes')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxCategoryModal;
